import { useEffect, useRef, useMemo } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import './ScrollReveal.css';

gsap.registerPlugin(ScrollTrigger);

const ScrollReveal = ({
  children,
  text,
  enableBlur = true,
  baseOpacity = 0.1,
  baseRotation = 3,
  blurStrength = 4,
  containerClassName = '',
  textClassName = '',
  rotationEnd = 'bottom bottom',
  wordAnimationEnd = 'bottom bottom'
}) => {
  const containerRef = useRef(null);

  const splitText = useMemo(() => {
    // Use text prop if provided, otherwise try to extract string from children
    let content = text;
    if (!content) {
      if (typeof children === 'string') {
        content = children;
      } else if (Array.isArray(children)) {
        content = children.join('');
      } else if (children?.props?.children) {
        content = children.props.children;
      } else {
        content = String(children || '');
      }
    }
    
    return content.split(/(\s+)/).map((word, index) => {
      if (word.match(/^\s+$/)) return word;
      return (
        <span className="scroll-reveal-word" key={index}>
          {word}
        </span>
      );
    });
  }, [children, text]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const wordElements = el.querySelectorAll('.scroll-reveal-word');
    console.log('ScrollReveal: Found', wordElements.length, 'words');
    if (wordElements.length === 0) return;

    // Set initial states
    gsap.set(el, { transformOrigin: '0% 50%', rotate: baseRotation });
    gsap.set(wordElements, { 
      opacity: baseOpacity, 
      filter: enableBlur ? `blur(${blurStrength}px)` : 'none'
    });
    console.log('ScrollReveal: Initial states set - opacity:', baseOpacity, 'blur:', blurStrength);

    // Create a context for easy cleanup
    const ctx = gsap.context(() => {
      // Rotation animation
      gsap.to(el, {
        rotate: 0,
        ease: 'none',
        scrollTrigger: {
          trigger: el,
          start: 'top 80%',
          end: 'top 20%',
          scrub: 0.5
        }
      });

      // Opacity and blur animation for each word
      wordElements.forEach((word, i) => {
        gsap.to(word, {
          opacity: 1,
          filter: enableBlur ? 'blur(0px)' : 'none',
          ease: 'none',
          scrollTrigger: {
            trigger: el,
            start: `top ${99 - (i * 1.5)}%`,
            end: `top ${95 - (i * 1)}%`,
            scrub: 0.5
          }
        });
      });
    }, el);

    // Refresh ScrollTrigger after a short delay to ensure proper positioning
    setTimeout(() => {
      ScrollTrigger.refresh();
      console.log('ScrollReveal: ScrollTrigger refreshed');
    }, 100);

    return () => ctx.revert();
  }, [enableBlur, baseRotation, baseOpacity, rotationEnd, wordAnimationEnd, blurStrength]);

  return (
    <div ref={containerRef} className={`scroll-reveal ${containerClassName}`}>
      <p className={`scroll-reveal-text ${textClassName}`}>{splitText}</p>
    </div>
  );
};

export default ScrollReveal;
